'use client'

import { useState } from 'react'
import { useExtracted } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Check, Copy, ExternalLink } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

// Verbatim across all locales — never wrap in t().
const MCP_COMMAND = 'claude mcp add --transport http --scope user martini https://www.martini.film/mcp'
const MARTINI_MCP_URL = 'https://www.martini.film/mcp'
const REPO_URL = 'https://github.com/Martini-Film/skills'
const BLENDER_URL = 'https://www.blender.org/download/'
const BLENDER_MCP_URL = 'https://github.com/ahujasid/blender-mcp'
const DEMO_VIDEO_URL =
  'https://reception.martini.film/storage/v1/object/public/public-assets/Skills/20260629-MotionControl-v3d.mp4'

const AGENTS = [
  // i18n-ignore -- agent product names, verbatim in every locale
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'codex', label: 'Codex' },
  { id: 'cursor', label: 'Cursor' },
] as const
type AgentId = (typeof AGENTS)[number]['id']

const installCommand = (agent: AgentId) =>
  `npx -y skills add github.com/martini-film/skills --global --agent ${agent} --yes`

function CopyButton({ active, onCopy, label }: { active: boolean; onCopy: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={label}
      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      {active ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

function CommandLine({
  command,
  id,
  copied,
  onCopy,
  label,
  prompt = true,
}: {
  command: string
  id: string
  copied: string | null
  onCopy: (id: string, v: string) => void
  label: string
  prompt?: boolean
}) {
  return (
    <div className="relative">
      <pre className="rounded-md border border-border bg-background py-2.5 pl-3 pr-11 font-mono text-xs leading-5 text-muted-foreground">
        <code className="whitespace-pre-wrap break-words">
          {prompt ? <span className="select-none text-muted-foreground/50">$ </span> : null}
          {command}
        </code>
      </pre>
      <CopyButton active={copied === id} onCopy={() => onCopy(id, command)} label={label} />
    </div>
  )
}

/**
 * The Blender skill page body at `/skills/blender`: public, no auth, rendered
 * under the Learn chrome (Skills tab alongside Docs/Tutorials) whose 48px
 * sticky header the hero's top padding accounts for.
 */
export function BlenderSkillClient() {
  const t = useExtracted()
  const [copied, setCopied] = useState<string | null>(null)
  const [agent, setAgent] = useState<AgentId>('claude-code')
  const copyLabel = t('Copy')

  const copy = (id: string, value: string) => {
    void navigator.clipboard.writeText(value)
    setCopied(id)
    window.setTimeout(() => setCopied(current => (current === id ? null : current)), 1600)
  }

  // The one thing a user pastes into their agent. It self-handles setup (install the
  // skill, get the MCPs connected — or have the agent walk the user through it, never
  // computer-use), then runs the shot. Deferred to English; the URLs stay verbatim.
  const agentPrompt = t(
    "If you don't already have the Blender → Martini skill, install it from github.com/martini-film/skills with the skills CLI for your agent. Make sure Blender MCP and Martini MCP (https://www.martini.film/mcp) are connected — if either isn't, walk me through connecting it and don't try to operate Blender with computer use. Then, in Blender via MCP: model a rough martini glass and shaker from rectangle primitives, add a camera move around them, and animate the shaker so it's dynamic. Render a basic 120-frame viewport guide, then use the Blender → Martini skill to bring it into Martini as an editable, camera-faithful draft. Do not start paid generation; leave the draft ready for me to review and generate when I choose.",
  )

  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="mx-auto grid w-full max-w-6xl items-center gap-12 px-6 pb-16 pt-14 sm:px-8 lg:grid-cols-2 lg:px-12">
        <div className="flex min-w-0 flex-col gap-6">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{t('Agent skill')}</span>
          <h1 className="text-5xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-6xl">
            {t('Blender → Martini')}
          </h1>
          <p className="max-w-md text-base leading-7 text-muted-foreground">
            {t(
              'A skill for your agent. Block out a shot in Blender, then bring it into Martini as an editable draft that preserves the camera you keyframed.',
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" className="h-9 rounded-md" onClick={() => copy('hero', agentPrompt)}>
              {copied === 'hero' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied === 'hero' ? t('Copied') : t('Copy the prompt')}
            </Button>
            <Button asChild variant="ghost" className="h-9 rounded-md">
              <a href={REPO_URL} target="_blank" rel="noopener noreferrer">
                {t('View on GitHub')}
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 justify-center lg:justify-end">
          <video
            className="max-h-[560px] w-auto rounded-lg border border-border bg-card"
            src={DEMO_VIDEO_URL}
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="metadata"
          />
        </div>
      </section>

      {/* Run it — paste-first */}
      <section className="mx-auto max-w-2xl px-6 pb-28 sm:px-8 lg:px-12">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {t('Paste this into your agent')}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {t(
            'Claude Code, cowork, Codex, Cursor. It installs the skill, connects the MCPs, and prepares a camera-faithful draft without spending on generation.',
          )}
        </p>

        {/* The paste block — monospaced so it reads as something for the agent. */}
        <div className="relative mt-5">
          <p className="rounded-md border border-border bg-background py-4 pl-4 pr-12 font-mono text-xs leading-6 text-foreground">
            {agentPrompt}
          </p>
          <CopyButton active={copied === 'prompt'} onCopy={() => copy('prompt', agentPrompt)} label={copyLabel} />
        </div>

        {/* Manual / CLI path — secondary, under the paste block. */}
        <div className="mt-8 rounded-md border border-border bg-card p-5">
          <p className="text-sm font-medium text-foreground">{t('Or set it up manually')}</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            {t('Connect your agent, then run the prompt above (or any shot you like).')}
          </p>

          {/* Agent picker — swaps the commands. */}
          <div
            role="group"
            aria-label={t('Agent')}
            className="mt-4 inline-flex rounded-md border border-border bg-background p-0.5"
          >
            {AGENTS.map(a => (
              <button
                key={a.id}
                type="button"
                aria-pressed={agent === a.id}
                onClick={() => {
                  setAgent(a.id)
                  setCopied(null) // ids are shared across tabs; clear so the checkmark can't carry over
                }}
                className={cn(
                  'rounded-[5px] px-3 py-1 text-xs font-medium transition-colors',
                  agent === a.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {a.label}
              </button>
            ))}
          </div>

          <div className="mt-5 space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{t('Install the skill')}</p>
              <CommandLine
                command={installCommand(agent)}
                id="install"
                copied={copied}
                onCopy={copy}
                label={copyLabel}
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{t('Connect Martini MCP')}</p>
              {agent === 'claude-code' ? (
                <CommandLine command={MCP_COMMAND} id="mcp" copied={copied} onCopy={copy} label={copyLabel} />
              ) : (
                <>
                  <p className="text-sm leading-6 text-muted-foreground">{t('Add this URL in your MCP settings:')}</p>
                  <CommandLine
                    command={MARTINI_MCP_URL}
                    id="mcp"
                    copied={copied}
                    onCopy={copy}
                    label={copyLabel}
                    prompt={false}
                  />
                </>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{t('Blender + Blender MCP')}</p>
              <p className="text-sm leading-6 text-muted-foreground">
                {t('So your agent can read your scene and render the guide.')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="secondary" size="sm" className="h-8 rounded-md">
                  <a href={BLENDER_URL} target="_blank" rel="noopener noreferrer">
                    {t('Download Blender')}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
                <Button asChild variant="secondary" size="sm" className="h-8 rounded-md">
                  <a href={BLENDER_MCP_URL} target="_blank" rel="noopener noreferrer">
                    {t('Set up Blender MCP')}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <p className="mt-5 border-t border-border pt-3 text-xs leading-5 text-muted-foreground">
            {t(
              "Not connected yet? The prompt above tells your agent to walk you through it — it won't try to click around Blender.",
            )}
          </p>
        </div>
      </section>
    </div>
  )
}
